/**
 * Voided-payment predicate.
 *
 * `installer_payments.payment_state` carries TWO spellings for the same thing.
 * The server's void path writes `'voided'` (JobviousServer `invoiceModel.js:1619`),
 * while parts of the client were written against `'void'`. A `!== 'void'` test
 * therefore treats a genuinely voided row as live, which is the opposite of what
 * every call site intends:
 *
 *   - `isActiveDepositRow` counted a voided deposit as active, so a job whose
 *     deposit had been voided could never be re-invoiced — the exact failure
 *     `isActiveDepositRow` was written to prevent.
 *   - The split-payment reconciliation at `requestimates.jsx:5387` excluded
 *     voided rows from collected-so-far. Missing them counts a declined or
 *     cancelled terminal attempt as money received and UNDERSTATES the balance
 *     still owed.
 *
 * `newFinancials.jsx` had already hit this and grown a local `isVoidedLike`.
 * This is that predicate, promoted, so the next call site inherits it instead of
 * writing a fifth copy.
 *
 * Accepts both spellings deliberately: both mean void, and being permissive here
 * can only ever exclude more void rows — never include a live one.
 */
export const isVoidedPaymentState = (state) => state === 'voided' || state === 'void';

/**
 * Rows that must never count as a LIVE payment.
 *
 * Wider than `isVoidedPaymentState` by one value: `'deleted'`. Mirrors the
 * server's deposit guard, which excludes `NOT IN ('void','voided','deleted')`
 * across the four `depositStatus` subqueries — prod carries all three (25 void,
 * 3 voided, 1 deleted). **Change both in the same commit**, the same rule that
 * governs `SERVER_DUP_INACTIVE_STATES` in `duplicateDetection.js`.
 *
 * Kept separate from `isVoidedPaymentState` on purpose: that one drives the
 * financials "Voided" display filter, where a deleted row is not a voided one
 * and folding it in would mislabel it.
 */
export const isInactivePaymentState = (state) =>
  isVoidedPaymentState(state) || state === 'deleted';

/**
 * Money collected, no invoice document — the row that needs a human.
 *
 * Derivable entirely from fields the Financials feed already returns
 * (`installer_payments.*`), so this needs no server change:
 * `payment_state === 'paid'` AND no `invoice_id`. That is the same rule the
 * server's `get_draft` uses to compute `settledOffline`, which until now was
 * only reachable per-request via `GET /invoices/draft/:requestId` — unusable
 * from a list.
 *
 * ⚠️ `payment_method === 'External'` is EXCLUDED, and this exclusion is
 * load-bearing. Production carries 3,524 such rows worth ~$4.5M: the AK Wraps
 * and Ceramic Pro history ingests, imported already-settled and correctly
 * carrying no invoice. Without this test the report presents four and a half
 * million dollars of 2023 history as an emergency and is instantly worthless.
 * The real gap on prod is 13 rows.
 *
 * NOT a failure indicator. Cash and check settle without a Stripe invoice by
 * design (`invoiceController.js:1238`) — that money never moves through Stripe.
 * These rows need an OUTCOME recorded, not a retry: the server raises an
 * `invoice_recovery` task whose completion requires a `resolution_type`
 * (invoice_created_externally / written_off / duplicate_payment / refunded /
 * customer_unreachable). This flag is how the row gets FOUND; the task is where
 * it gets RESOLVED.
 */
export const isMissingInvoice = (row) => {
  const r = row || {};
  const method = String(r.payment_method ?? '').trim().toLowerCase();
  if (method === 'external') return false;
  if (r.payment_state !== 'paid') return false;
  return !r.invoice_id;
};

export default isVoidedPaymentState;
